/**
 * Calendar settings of the user (stored by the platform API, so they follow the user): loaded when the calendar
 * opens, changed optimistically; quick repeated changes (Ctrl+wheel zoom) are saved once after a short pause.
 */
import { toast } from '@platform/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { DEFAULT_CALENDAR_SETTINGS, type CalendarSettings, type CalendarSettingsPatch } from '../api/calendar-extra';

/** Changes within this time are saved together. */
const SAVE_DELAY_MS = 600;

export const TIME_SCALES = { compact: 2.25, normal: 3, large: 4 } as const;
export type TimeScale = keyof typeof TIME_SCALES;
export const MIN_HOUR_HEIGHT = 1.5;
export const MAX_HOUR_HEIGHT = 8;

export function useCalendarSettings(active: boolean, failedMessage: string) {
  const [settings, setSettings] = useState<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const pending = useRef<CalendarSettingsPatch>({});
  const adopted = useRef<CalendarSettingsPatch>({});
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    api.calendarSettings().then(
      (value) => {
        if (cancelled) return;
        // Changes made while loading win.
        setSettings({ ...value, ...adopted.current, ...pending.current });
        setLoaded(true);
      },
      () => undefined, // Defaults stay; the calendar still works.
    );
    return () => {
      cancelled = true;
    };
  }, [active, loaded]);

  const flush = useCallback(() => {
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0) return;
    api.updateCalendarSettings(patch).catch(() => toast.error(failedMessage));
  }, [failedMessage]);

  useEffect(() => () => {
    clearTimeout(timer.current);
    flush();
  }, [flush]);

  const update = useCallback(
    (patch: CalendarSettingsPatch) => {
      setSettings((current) => ({ ...current, ...patch }));
      pending.current = { ...pending.current, ...patch };
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DELAY_MS);
    },
    [flush],
  );

  const adoptSettings = useCallback((patch: CalendarSettingsPatch) => {
    // A completed atomic regional Save supersedes unsent edits and a stale initial GET.
    adopted.current = { ...adopted.current, ...patch };
    for (const key of Object.keys(patch) as (keyof CalendarSettingsPatch)[]) delete pending.current[key];
    setSettings((current) => ({ ...current, ...patch }));
  }, []);
  return { settings, loaded, updateSettings: update, adoptSettings };
}

/** Which preset matches the hour height (undefined = a zoomed value in between). */
export const timeScaleOf = (hourHeight: number): TimeScale | undefined => (Object.keys(TIME_SCALES) as TimeScale[]).find((scale) => TIME_SCALES[scale] === hourHeight);

/** Zoom step of Ctrl+wheel / pinch: ±0.25 rem per hour, within the limits. */
export const zoomHourHeight = (current: number, direction: 1 | -1): number => Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, Math.round((current + direction * 0.25) * 4) / 4));
